import { Button, Flex, Form } from "antd"
import { Modal } from "ui/Modal"
import { useTranslation } from "react-i18next"
import { spacings } from "themes/variables"
import { useMemo } from "react"
import PropTypes from "prop-types"
import Input from "components/form/Input"
import { PhoneInput, validatePhoneNumber } from "ui/Input/PhoneInput"
import LabeledField from "../../Tabs/ProfileInfo/LabeledField"
import { isValidPhoneNumber } from "libphonenumber-js"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { useSelector } from "react-redux"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"

const UpdateMobileNumber = ({
  open,
  onClose,
  setOpen,
  hasSentCode,
  cooldown,
  isSending,
  sendCodeLabel,
  handleSendCode,
  handleConfirmCode,
  handleUpdateReset,
  resetKey,
  phone_number,
  phoneValue,
  onPhoneChange
}) => {
  const { t } = useTranslation()
  const form = Form.useFormInstance()
  const codeValue = Form.useWatch("input_code", form)
  const isLoading = useSelector((state) => state.user.verifyOtpForPhoneNumberUpdation.loading)
  const isSendOtpLoading = useSelector((state) => state.user.sendOtpToVerifyPhone.loading)

  const { isMobile } = useDevice()
  const canSendCode = useMemo(
    () => isValidPhoneNumber(phoneValue?.fullNumber || "") && phoneValue?.fullNumber !== phone_number,
    [phoneValue?.fullNumber, phone_number]
  )
  const modalContent = () => {
    return (
      <FirstLoading loading={isLoading}>
        <Flex vertical className="mb-m" gap={spacings.S}>
          <LabeledField
            labelKey="client.profile.tabs.profile.enter_new_phone_number"
            name="update_phone_number"
            rules={[
              {
                validator: (_, value) => {
                  if (!value?.fullNumber) {
                    return Promise.reject(t("form_validation.phone_required"))
                  }
                  return validatePhoneNumber(value.fullNumber)
                }
              }
            ]}
          >
            <PhoneInput
              key={`phone-${resetKey}`}
              initialCountryCode={phoneValue?.countryCode}
              initialValue={phoneValue?.fullNumber}
              onChange={onPhoneChange}
              disabled={hasSentCode}
            />
          </LabeledField>
          <Flex gap={spacings.S}>
            <Button type="primary" onClick={handleSendCode} disabled={!canSendCode || isSending || cooldown > 0} loading={isSendOtpLoading}>
              {sendCodeLabel}
            </Button>
            {hasSentCode && (
              <Button color="primary" variant="outlined" onClick={handleUpdateReset}>
                {t("global.cancel")}
              </Button>
            )}
          </Flex>
          {hasSentCode && (
            <Flex vertical>
              <LabeledField
                name="input_code"
                labelKey="client.profile.tabs.profile.input_code"
                rules={[{ required: true, message: t("client.profile.tabs.profile.code_required") }]}
              >
                <Input type="number" />
              </LabeledField>
              <Button
                type="primary"
                onClick={handleConfirmCode}
                className="w-fit"
                disabled={!codeValue || String(codeValue).trim().length === 0}
              >
                {t("global.confirm")}
              </Button>
            </Flex>
          )}
        </Flex>
      </FirstLoading>
    )
  }

  return (
    <>
      {isMobile ? (
        <CustomDrawer
          open={open}
          setOpen={setOpen}
          onCancel={onClose}
          onClose={onClose}
          title={t("client.profile.tabs.profile.update_phone_number")}
        >
          {modalContent()}
        </CustomDrawer>
      ) : (
        <Modal title={t("client.profile.tabs.profile.update_phone_number")} open={open} onCancel={onClose} width={600} centered>
          {modalContent()}
        </Modal>
      )}
    </>
  )
}

UpdateMobileNumber.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  hasSentCode: PropTypes.bool.isRequired,
  cooldown: PropTypes.number.isRequired,
  isSending: PropTypes.bool.isRequired,
  sendCodeLabel: PropTypes.string.isRequired,
  handleSendCode: PropTypes.func.isRequired,
  handleConfirmCode: PropTypes.func.isRequired,
  handleUpdateReset: PropTypes.func.isRequired,
  resetKey: PropTypes.number.isRequired,
  phone_number: PropTypes.string.isRequired,
  phoneValue: PropTypes.string.isRequired,
  onPhoneChange: PropTypes.func.isRequired
}

export default UpdateMobileNumber
